/**
 * Scripts — Unified scripts list page.
 * Replaces Dashboard, Automations, and Extension Scripts pages.
 * Groups scripts by app with search, filter, and action buttons.
 * Matches the ScriptsListMockup UI but wired to the real API.
 */

import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Avatar, Box, Typography, Paper, TextField, Chip, IconButton, Table, TableHead,
  TableRow, TableCell, TableBody, TableContainer, Collapse, InputAdornment,
  MenuItem, Select, FormControl, InputLabel, Tooltip, Badge,
  CircularProgress, Alert, Button, Snackbar, Dialog, DialogTitle, DialogContent,
  DialogActions, DialogContentText, FormControlLabel, Checkbox,
} from '@mui/material';
import {
  Edit as EditIcon, PlayArrow as PlayIcon, Visibility as WatchIcon,
  Schedule as ScheduleIcon, History as HistoryIcon, Search as SearchIcon,
  KeyboardArrowDown as ExpandIcon, KeyboardArrowUp as CollapseIcon,
  CheckCircle as PassIcon, Cancel as FailIcon,
  FiberManualRecord as DotIcon, CloudDownload as HeadlessIcon,
  Shield as ShieldIcon, Warning as WarningIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { fetchExtensionScripts, fetchExecutionHistory } from '../redux/thunks/extensionScriptsThunks';
import extensionScriptsService from '../services/api/extensionScriptsService';
import networkRulesService from '../services/api/networkRulesService';
import appRegistryService from '../services/api/appRegistryService';
import { ExecutorPlayButton, ExecutorModeChip } from '../components/ExecutorModeControls';

// Script-type colors matching the mockup
const TYPE_COLORS = {
  login: { bg: '#e3f2fd', color: '#1565c0', label: 'Login' },
  progress: { bg: '#e8f5e9', color: '#2e7d32', label: 'Progress' },
  roster: { bg: '#fff3e0', color: '#e65100', label: 'Roster' },
  'de-roster': { bg: '#fce4ec', color: '#c62828', label: 'De-roster' },
  'account-creation': { bg: '#f3e5f5', color: '#6a1b9a', label: 'Account Creation' },
  content_interaction: { bg: '#e0f7fa', color: '#00695c', label: 'Content' },
  data_extraction: { bg: '#eceff1', color: '#455a64', label: 'Data Extraction' },
  custom: { bg: '#eceff1', color: '#455a64', label: 'Custom' },
};

const STATUS_ICONS = {
  passed: <DotIcon sx={{ color: '#4caf50', fontSize: 14 }} />,
  completed: <DotIcon sx={{ color: '#4caf50', fontSize: 14 }} />,
  failed: <DotIcon sx={{ color: '#f44336', fontSize: 14 }} />,
  never: <DotIcon sx={{ color: '#bdbdbd', fontSize: 14 }} />,
};

const FILTER_TYPES = ['All', 'Login', 'Progress', 'Roster', 'De-roster', 'Account Creation'];

/** Send a message to the Automatrix Chrome extension via postMessage bridge */
const sendToExtension = (payload) => {
  return new Promise((resolve, reject) => {
    const requestId = `req_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    const timeout = setTimeout(() => {
      window.removeEventListener('message', handler);
      reject(new Error('Extension did not respond within 30 seconds. Is the extension installed?'));
    }, 30000);

    const handler = (event) => {
      if (event.source !== window) return;
      const data = event.data;
      if (!data || data.source !== 'automatrix-extension') return;
      if (data.requestId !== requestId) return;
      clearTimeout(timeout);
      window.removeEventListener('message', handler);
      resolve(data.payload);
    };

    window.addEventListener('message', handler);
    window.postMessage({ source: 'automatrix-frontend', requestId, payload }, '*');
  });
};

const Scripts = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { scripts: reduxScripts, loading: reduxLoading } = useSelector(
    (state) => state.extensionScripts || {}
  );

  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('All');
  const [appFilter, setAppFilter] = useState('All Apps');
  const [expandedRow, setExpandedRow] = useState(null);
  const [executions, setExecutions] = useState({});
  const [executionsLoading, setExecutionsLoading] = useState({});
  const [filterSummary, setFilterSummary] = useState({});
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState(null);
  // [icons-audit] App logos keyed by configKey, sourced from /apps-overview
  // so each group header + row can render the app's icon with a fallback.
  const [appsOverview, setAppsOverview] = useState([]);

  // Create a blank script and jump to the editor.
  const handleCreateBlank = useCallback(async () => {
    setCreating(true);
    setCreateError(null);
    try {
      const { data } = await extensionScriptsService.create({
        name: 'Untitled Script',
        urlPatterns: [],
        script: '// Populate via recording or manual steps',
        steps: [],
        isActive: true,
      });
      if (!data?.id) throw new Error('Server did not return a script id');
      navigate(`/scripts/${data.id}/edit`);
    } catch (err) {
      console.error('Failed to create blank script:', err);
      setCreateError(err?.response?.data?.message || err.message || 'Failed to create script');
    } finally {
      setCreating(false);
    }
  }, [navigate]);

  // Fetch all scripts and filter summary on mount
  useEffect(() => {
    dispatch(fetchExtensionScripts());
    networkRulesService.getRuleSummaryByApp()
      .then(setFilterSummary)
      .catch(() => {});
    // [icons-audit] Pull apps-overview for logo lookups. This endpoint is
    // cheap (list read) and powers the icon next to each app group header.
    appRegistryService.getOverview()
      .then((res) => setAppsOverview(Array.isArray(res.data) ? res.data : []))
      .catch(() => {});
  }, [dispatch]);

  // [icons-audit] Lookup map: configKey → { logo, name }
  const appLogoByKey = useMemo(() => {
    const map = {};
    (appsOverview || []).forEach((a) => {
      if (a?.configKey) {
        map[a.configKey] = { logo: a.logo || a.icon || null, name: a.displayName || a.name };
      }
    });
    return map;
  }, [appsOverview]);

  const scripts = useMemo(() => {
    return Array.isArray(reduxScripts) ? reduxScripts : [];
  }, [reduxScripts]);

  // Derive unique app names for filter dropdown
  const appNames = useMemo(() => {
    const set = new Set(scripts.map((s) => s.appName || s.appKey || 'Unknown'));
    return ['All Apps', ...Array.from(set).sort()];
  }, [scripts]);

  // Filter scripts
  const filteredScripts = useMemo(() => {
    return scripts.filter((s) => {
      if (search) {
        const q = search.toLowerCase();
        const name = (s.name || '').toLowerCase();
        const app = (s.appName || s.appKey || '').toLowerCase();
        if (!name.includes(q) && !app.includes(q)) return false;
      }
      if (typeFilter !== 'All') {
        const mapped = {
          'Login': 'login',
          'Progress': 'progress',
          'Roster': 'roster',
          'De-roster': 'de-roster',
          'Account Creation': 'account-creation',
        };
        const targetType = mapped[typeFilter];
        if (targetType && s.type !== targetType && s.scriptType !== targetType) return false;
      }
      if (appFilter !== 'All Apps') {
        const scriptApp = s.appName || s.appKey || 'Unknown';
        if (scriptApp !== appFilter) return false;
      }
      return true;
    });
  }, [scripts, search, typeFilter, appFilter]);

  // Group by app
  const grouped = useMemo(() => {
    const groups = {};
    filteredScripts.forEach((s) => {
      const key = s.appKey || 'unknown';
      if (!groups[key]) {
        groups[key] = { appName: s.appName || s.appKey || 'Unknown', scripts: [] };
      }
      groups[key].scripts.push(s);
    });
    return groups;
  }, [filteredScripts]);

  // Load execution history when expanding a row
  const handleExpandRow = useCallback(
    (scriptId) => {
      if (expandedRow === scriptId) {
        setExpandedRow(null);
        return;
      }
      setExpandedRow(scriptId);
      if (!executions[scriptId]) {
        setExecutionsLoading((prev) => ({ ...prev, [scriptId]: true }));
        extensionScriptsService
          .getExecutions(scriptId, { limit: 5 })
          .then(({ data }) => {
            const items = Array.isArray(data) ? data : data.items || [];
            setExecutions((prev) => ({ ...prev, [scriptId]: items }));
          })
          .catch((err) => {
            console.error('Failed to load executions:', err);
            setExecutions((prev) => ({ ...prev, [scriptId]: [] }));
          })
          .finally(() => {
            setExecutionsLoading((prev) => ({ ...prev, [scriptId]: false }));
          });
      }
    },
    [expandedRow, executions]
  );

  const getLastStatus = (script) => {
    // Check if we have executions loaded
    const execs = executions[script.id];
    if (execs && execs.length > 0) {
      return execs[0].status === 'completed' ? 'passed' : execs[0].status || 'never';
    }
    return script.lastStatus || 'never';
  };

  const getLastRun = (script) => {
    const execs = executions[script.id];
    if (execs && execs.length > 0) {
      const d = execs[0].startedAt || execs[0].createdAt;
      return d ? new Date(d).toLocaleString() : 'Never';
    }
    return script.lastRun || 'Never';
  };

  const getExecMode = (script) => {
    if (script.executionMode) {
      return script.executionMode.charAt(0).toUpperCase() + script.executionMode.slice(1);
    }
    return 'Frontend';
  };

  const getScriptType = (script) => {
    return script.scriptType || script.type || 'login';
  };

  const [pendingScriptId, setPendingScriptId] = useState(null);

  // Stage script in extension. If the sidebar is already open, skip the dialog
  // and open the edit page directly. If not, show a prompt so the user knows
  // to open the sidebar first.
  const handleRunInExtension = useCallback(async (scriptId, e) => {
    e.stopPropagation();
    // Push auth token so sidebar doesn't show the auth gate
    try {
      const token = localStorage.getItem('token');
      if (token) await sendToExtension({ action: 'SET_AUTH_TOKEN', token }).catch(() => {});
    } catch {}
    // Stage the script (extension receives it but doesn't execute)
    try {
      await sendToExtension({ action: 'RUN_SCRIPT', scriptId });
    } catch (err) {
      alert('Could not reach extension: ' + err.message);
      return;
    }
    // If sidebar is already open, go straight to the edit page in a new tab
    if (localStorage.getItem('skipSidebarPrompt') === '1') {
      window.open(`/scripts/${scriptId}/edit`, '_blank');
      return;
    }
    try {
      const resp = await sendToExtension({ action: 'IS_SIDE_PANEL_OPEN' });
      if (resp?.open) {
        window.open(`/scripts/${scriptId}/edit`, '_blank');
        return;
      }
    } catch {}
    // Sidebar not detected — show a hint to open it
    setPendingScriptId(scriptId);
  }, []);

  // Handle headless run. `mode` defaults to 'playwright' (existing behaviour);
  // the split-button chevron lets the user pick 'extension-v5' instead.
  const handleRunHeadless = useCallback(async (scriptId, mode = 'playwright') => {
    try {
      await extensionScriptsService.runByExecutor(scriptId, mode);
    } catch (err) {
      console.error('Failed to trigger headless run:', err);
    }
  }, []);

  if (reduxLoading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', p: 4 }}>
        <CircularProgress size={28} sx={{ mr: 1 }} />
        <Typography variant="body1">Loading scripts...</Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, mb: 3 }}>
        <Typography variant="h4" fontWeight={700}>Scripts</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 2, width: { xs: '100%', sm: 'auto' } }}>
          <TextField
            size="small"
            placeholder="Search scripts..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{ width: { xs: '100%', sm: 260 } }}
          />
          <FormControl size="small" sx={{ minWidth: 160, flex: { xs: 1, sm: 'none' } }}>
            <InputLabel>App</InputLabel>
            <Select value={appFilter} label="App" onChange={(e) => setAppFilter(e.target.value)}>
              {appNames.map((a) => (
                <MenuItem key={a} value={a}>{a}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            variant="contained"
            color="primary"
            startIcon={creating ? <CircularProgress size={16} color="inherit" /> : <AddIcon />}
            onClick={handleCreateBlank}
            disabled={creating}
            sx={{ whiteSpace: 'nowrap' }}
          >
            New Script
          </Button>
        </Box>
      </Box>
      {createError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setCreateError(null)}>
          {createError}
        </Alert>
      )}

      {/* Type filter chips */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
        {FILTER_TYPES.map((t) => (
          <Chip
            key={t}
            label={t}
            variant={typeFilter === t ? 'filled' : 'outlined'}
            color={typeFilter === t ? 'primary' : 'default'}
            onClick={() => setTypeFilter(t)}
            sx={{ fontWeight: typeFilter === t ? 700 : 400 }}
          />
        ))}
      </Box>

      {/* Empty state */}
      {Object.keys(grouped).length === 0 && !reduxLoading && (
        <Alert severity="info" sx={{ mt: 2 }}>
          No scripts match your filters. Try adjusting search or filter criteria.
        </Alert>
      )}

      {/* Script groups */}
      {Object.entries(grouped).map(([appKey, group]) => {
        // [icons-audit] Resolve app logo from configKey lookup. Falls back to
        // a deterministic initials avatar when no logo is available.
        const appMeta = appLogoByKey[appKey];
        const logoUrl = appMeta?.logo;
        const initial = (group.appName || appKey || '?').charAt(0).toUpperCase();
        return (
        <Paper key={appKey} sx={{ mb: 3, overflow: 'hidden' }} elevation={2}>
          <Box sx={{ bgcolor: '#f5f5f5', px: 2, py: 1.5, borderBottom: '1px solid #e0e0e0', display: 'flex', alignItems: 'center', gap: 1 }}>
            {logoUrl ? (
              <Avatar src={logoUrl} alt={group.appName} variant="rounded" sx={{ width: 28, height: 28, bgcolor: 'grey.100' }} />
            ) : (
              <Avatar variant="rounded" sx={{ width: 28, height: 28, bgcolor: 'primary.light', fontSize: 14 }}>
                {initial}
              </Avatar>
            )}
            <Typography variant="subtitle1" fontWeight={700} sx={{ textTransform: 'capitalize' }}>
              {group.appName}
            </Typography>
            <Chip label={`${group.scripts.length} scripts`} size="small" />
            {(() => {
              // Find filter summary for this app (match by appKey against appDefinitionId values)
              const appEntry = Object.entries(filterSummary).find(([key]) => {
                // Match by appKey if the summary uses configKeys, or skip UUID matching
                return key === appKey;
              });
              const counts = appEntry?.[1];
              const total = counts ? (counts.networkRules || 0) + (counts.elementHideRules || 0) : 0;
              if (total > 0) {
                return (
                  <Tooltip title={`${counts.networkRules} network rules, ${counts.elementHideRules} element rules`}>
                    <Chip
                      icon={<ShieldIcon sx={{ fontSize: 14 }} />}
                      label={`${total} filters`}
                      size="small"
                      sx={{ bgcolor: '#e8f5e9', color: '#2e7d32', fontWeight: 600, fontSize: '0.7rem' }}
                    />
                  </Tooltip>
                );
              }
              return (
                <Tooltip title="No content filters configured for this app">
                  <Chip
                    icon={<WarningIcon sx={{ fontSize: 14 }} />}
                    label="No filters"
                    size="small"
                    sx={{ bgcolor: '#fff3e0', color: '#e65100', fontSize: '0.7rem' }}
                  />
                </Tooltip>
              );
            })()}
          </Box>
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell width={40} />
                  <TableCell sx={{ fontWeight: 700 }}>Script Name</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="center">Type</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="center">Mode</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="center">Version</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="center">Last Run</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="center">Status</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {group.scripts.map((script) => {
                  const scriptType = getScriptType(script);
                  const tc = TYPE_COLORS[scriptType] || TYPE_COLORS.custom;
                  const isExpanded = expandedRow === script.id;
                  const status = getLastStatus(script);
                  const statusIcon = STATUS_ICONS[status] || STATUS_ICONS.never;
                  const execMode = getExecMode(script);

                  return (
                    <React.Fragment key={script.id}>
                      <TableRow
                        hover
                        sx={{
                          cursor: 'pointer',
                          '& > *': { borderBottom: isExpanded ? 'none' : undefined },
                        }}
                        onClick={() => handleExpandRow(script.id)}
                      >
                        <TableCell>
                          <IconButton size="small">
                            {isExpanded ? <CollapseIcon /> : <ExpandIcon />}
                          </IconButton>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight={600}>
                            {script.name}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label={tc.label}
                            size="small"
                            sx={{
                              bgcolor: tc.bg,
                              color: tc.color,
                              fontWeight: 600,
                              fontSize: '0.75rem',
                            }}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label={execMode}
                            size="small"
                            variant={execMode === 'Frontend' ? 'outlined' : 'filled'}
                            color={execMode === 'Backend' ? 'primary' : 'default'}
                            sx={{ fontSize: '0.7rem', height: 22 }}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Badge
                            color="info"
                            badgeContent={`v${script.version || '1.0'}`}
                            sx={{
                              '& .MuiBadge-badge': {
                                position: 'relative',
                                transform: 'none',
                                fontSize: '0.7rem',
                              },
                            }}
                          >
                            <span />
                          </Badge>
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="caption" color="text.secondary">
                            {getLastRun(script)}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                            {statusIcon}
                            <Typography variant="caption" sx={{ textTransform: 'capitalize' }}>
                              {status}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell align="right">
                          <Box
                            sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0 }}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <Tooltip title="Edit">
                              <IconButton
                                size="small"
                                onClick={() => navigate(`/scripts/${script.id}/edit`)}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title={execMode === 'Backend' ? 'Run (Watch Mode)' : 'Run (Extension)'}>
                              <IconButton
                                size="small"
                                color="success"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (script.executionMode === 'backend') {
                                    navigate(`/scripts/${script.id}/edit?watch=true`);
                                  } else {
                                    handleRunInExtension(script.id, e);
                                  }
                                }}
                              >
                                <PlayIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            {/* Headless run — split-button picks the executor (Playwright vs v5 ext). */}
                            <ExecutorPlayButton
                              tooltip="Run Headless (chevron picks Playwright vs v5 ext)"
                              color="info"
                              onRun={(mode) => handleRunHeadless(script.id, mode)}
                            />
                            <Tooltip title="Watch Mode">
                              <IconButton
                                size="small"
                                color="info"
                                onClick={() => navigate(`/scripts/${script.id}/edit?watch=true`)}
                              >
                                <WatchIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="History">
                              <IconButton size="small">
                                <HistoryIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>

                      {/* Expanded execution history */}
                      <TableRow>
                        <TableCell colSpan={8} sx={{ py: 0, px: 0 }}>
                          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                            <Box sx={{ px: 4, py: 2, bgcolor: '#fafafa' }}>
                              <Typography variant="subtitle2" gutterBottom fontWeight={600}>
                                Recent Executions
                              </Typography>
                              {executionsLoading[script.id] ? (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
                                  <CircularProgress size={14} />
                                  <Typography variant="body2" color="text.secondary">
                                    Loading...
                                  </Typography>
                                </Box>
                              ) : (executions[script.id] || []).length === 0 ? (
                                <Typography variant="body2" color="text.secondary">
                                  No executions recorded yet.
                                </Typography>
                              ) : (
                                <Table size="small">
                                  <TableHead>
                                    <TableRow>
                                      <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                                      <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                                      <TableCell sx={{ fontWeight: 600 }}>Engine</TableCell>
                                      <TableCell sx={{ fontWeight: 600 }}>Duration</TableCell>
                                      <TableCell sx={{ fontWeight: 600 }}>Credential</TableCell>
                                      <TableCell sx={{ fontWeight: 600 }}>Steps</TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {(executions[script.id] || []).map((exec) => (
                                      <TableRow key={exec.id}>
                                        <TableCell>
                                          <Typography variant="caption">
                                            {exec.startedAt
                                              ? new Date(exec.startedAt).toLocaleString()
                                              : exec.date || ''}
                                          </Typography>
                                        </TableCell>
                                        <TableCell>
                                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                            {exec.status === 'passed' || exec.status === 'completed' ? (
                                              <PassIcon sx={{ color: '#4caf50', fontSize: 16 }} />
                                            ) : (
                                              <FailIcon sx={{ color: '#f44336', fontSize: 16 }} />
                                            )}
                                            <Typography variant="caption" sx={{ textTransform: 'capitalize' }}>
                                              {exec.status}
                                            </Typography>
                                          </Box>
                                        </TableCell>
                                        <TableCell>
                                          <ExecutorModeChip exec={exec} />
                                        </TableCell>
                                        <TableCell>
                                          <Typography variant="caption">
                                            {exec.duration || '-'}
                                          </Typography>
                                        </TableCell>
                                        <TableCell>
                                          <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                                            {exec.credential || exec.credentialId || '-'}
                                          </Typography>
                                        </TableCell>
                                        <TableCell>
                                          <Typography variant="caption">
                                            {exec.stepsCompleted !== undefined
                                              ? `${exec.stepsCompleted}/${exec.stepsTotal || '?'}`
                                              : exec.steps || '-'}
                                          </Typography>
                                        </TableCell>
                                      </TableRow>
                                    ))}
                                  </TableBody>
                                </Table>
                              )}
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
        );
      })}

      {/* Summary footer */}
      <Box sx={{ display: 'flex', gap: 3, mt: 2, color: 'text.secondary' }}>
        <Typography variant="body2">{filteredScripts.length} scripts shown</Typography>
        <Typography variant="body2">
          {filteredScripts.filter((s) => getLastStatus(s) === 'passed').length} passing
        </Typography>
        <Typography variant="body2">
          {filteredScripts.filter((s) => getLastStatus(s) === 'failed').length} failing
        </Typography>
        <Typography variant="body2">
          {filteredScripts.filter((s) => getLastStatus(s) === 'never').length} never run
        </Typography>
      </Box>

      {/* Confirm dialog — prompts user to open sidebar before script runs */}
      <Dialog open={!!pendingScriptId} onClose={() => setPendingScriptId(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Open the Automatrix sidebar</DialogTitle>
        <DialogContent>
          <DialogContentText>
            The script has been loaded into the extension. To run it:
          </DialogContentText>
          <DialogContentText component="ol" sx={{ mt: 1, pl: 3 }}>
            <li>Click the <strong>Automatrix icon</strong> in your Chrome toolbar (or pin it from the puzzle-piece menu)</li>
            <li>Pick a credential if needed</li>
            <li>Click <strong>Play</strong> in the sidebar to start</li>
          </DialogContentText>
          <FormControlLabel
            sx={{ mt: 2 }}
            control={
              <Checkbox
                onChange={(e) => {
                  if (e.target.checked) localStorage.setItem('skipSidebarPrompt', '1');
                  else localStorage.removeItem('skipSidebarPrompt');
                }}
              />
            }
            label="Don't ask again"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPendingScriptId(null)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => {
              const id = pendingScriptId;
              setPendingScriptId(null);
              window.open(`/scripts/${id}/edit`, '_blank');
            }}
          >
            Open Script
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Scripts;
